import React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useParams, Link } from 'react-router-dom';
import Button from '@material-ui/core/Button';
import Dialog from '@material-ui/core/Dialog';
import ArrowBack from '@material-ui/icons/ArrowBack';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import * as bookingsSelectors from 'selectors/bookings';
import { userData as userDataSelector } from 'selectors/user';
import * as actions from 'actions';
import Api from 'api';
import Main from 'components/templates/Main';
import Errors from 'components/shared/Errors';
import Success from 'components/shared/Success';
import FormSection from 'components/templates/FormSection';
import PATHS from 'constants/paths';
import moment from 'logic/moment';
import { cleanUpData } from 'logic/bookingForm';
import { cleanDacComplianceData } from 'logic/cottage';
import HostInfomationDialog from 'modules/users/containers/HostInformationDialog';
import CreateForm from '../components/CreateForm';
import CreatePreview from '../components/CreatePreview';
import DacComplianceAddress from '../../cottages/components/Forms/DacComplianceAddress';

const CreateBooking = () => {
  const { conversationId } = useParams();
  const [isPreview, setIsPreview] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [overlap, setOverlap] = React.useState([]);
  const [initialValues, setInitialValues] = React.useState({
    cleaning_policy: 'guest',
    check_in: moment().hour(12).minute(0).add(1, 'day').toISOString(),
    check_out: moment().hour(12).minute(0).add(2, 'day').toISOString(),
    mark_as_booked_if_accepted: true,
  });
  const [displayName, setDisplayName] = React.useState('');
  const [dacDialog, setDacDialog] = React.useState({ open: false, cottage: null, data: null });
  const dispatch = useDispatch();
  const cancellationPolicy = useSelector(bookingsSelectors.cancellationPolicy);
  const cottages = useSelector(bookingsSelectors.cottages);
  const currentUser = useSelector(userDataSelector);
  const isBookingStrict = useSelector(bookingsSelectors.isBookingStrict);
  const isPosting = useSelector(bookingsSelectors.isPosting);
  const isFetching = useSelector(bookingsSelectors.isFetching);
  const postError = useSelector(bookingsSelectors.error);
  const postSuccess = useSelector(bookingsSelectors.success);
  const users = useSelector((state) => state.conversation.data.Users);
  const disableSubmit = currentUser.type === 'SOFT';
  const fullscreen = useMediaQuery((theme) => theme.breakpoints.down('xs'));

  React.useEffect(() => {
    window.scrollTo(0, 0);

    if (!isFetching && !isPosting) {
      dispatch(actions.fetchBookingsInfoRequest({ conversationId }));
      dispatch(actions.fetchBookingsRequest({ conversationId }));
    }
  }, []);

  React.useEffect(() => {
    if (users && currentUser) {
      const otherUsers = users.filter((user) => user.id !== currentUser.id);

      setDisplayName(otherUsers.map((user) => user.name).join(', '));

      if (!initialValues.guest_id) {
        setInitialValues({
          ...initialValues,
          guest_id: String(otherUsers[0].id),
        });
      }
    }
  }, [currentUser, users]);

  const setPreviewMode = async (data, ignoreDac = false) => {
    const cottage = cottages.find((c) => c.id === data.cottage_id);

    if (!cottage.dac7_valid && !ignoreDac) {
      setDacDialog({ open: true, cottage, data });

      return;
    }

    setLoading(true);
    setOverlap([]);
    setIsPreview(true);
    setInitialValues(data);

    try {
      const { check_in, check_out, cottage_id } = data;

      const overlaping = await Api.overlapBooking(cottage_id, { check_in, check_out });

      setOverlap(overlaping);
    } catch (err) {
      setOverlap([]);
    }

    setLoading(false);
  };

  const onDacDialogClose = async () => {
    setDacDialog({ open: false, cottage: null, data: null });
  };

  const onDacSubmit = async (data) => {
    setLoading(false);

    try {
      await Api.updateCottage(dacDialog.cottage.id, cleanDacComplianceData(data));
      const preData = { ...dacDialog.data };

      setDacDialog({ open: false, cottage: null, data: null });
      setPreviewMode(preData, true);
    } catch (err) {
      console.log(err); // eslint-disable-line
    } finally {
      setLoading(false);
    }
  };

  const submitBookingForm = () => {
    const mergedData = {
      ...initialValues,
      conversation_id: conversationId,
    };

    dispatch(actions.postBookingRequest(cleanUpData(mergedData)));
  };

  const setEditMode = () => {
    setIsPreview(false);
    setLoading(false);
    setOverlap([]);
  };

  const completed = currentUser.dac7_valid;

  if (!completed) {
    return (
      <Main
        title={
          <Button
            color="inherit"
            component={Link}
            to={PATHS.conversations.bookings.replace(':conversationId', conversationId)}
          >
            <ArrowBack /> {displayName}
          </Button>
        }
      >
        <HostInfomationDialog />
      </Main>
    );
  }

  if (isPreview) {
    return (
      <Main
        title={
          <Button
            component={Link}
            to={PATHS.conversations.bookings.replace(':conversationId', conversationId)}
          >
            <ArrowBack /> {displayName}
          </Button>
        }
      >
        <div>
          <CreatePreview
            {...{
              submitBookingForm,
              currentUser,
              users,
              cottages,
              cancellationPolicy,
              setEditMode,
              overlap,
              loading,
              initialValues,
            }}
          />
        </div>
        <Success success={postSuccess} />
        <Errors errors={postError} />
      </Main>
    );
  }

  return (
    <Main
      title={
        <Button
          component={Link}
          to={PATHS.conversations.bookings.replace(':conversationId', conversationId)}
        >
          <ArrowBack /> {displayName}
        </Button>
      }
    >
      {dacDialog.open && (
        <>
          <Dialog
            fullScreen={fullscreen}
            fullWidth
            maxWidth="lg"
            onClose={onDacDialogClose}
            open={dacDialog.open}
          >
            <DacComplianceAddress
              onSubmit={onDacSubmit}
              cottage={dacDialog.cottage}
              isLoading={isPosting || isFetching || loading}
              onClose={onDacDialogClose}
            />
          </Dialog>
        </>
      )}

      <FormSection>
        <CreateForm
          {...{
            setPreviewMode,
            cancellationPolicy,
            cottages,
            initialValues,
            disableSubmit,
            isBookingStrict,
            conversationId,
          }}
        />
      </FormSection>
      <Success success={postSuccess} />
      <Errors errors={postError} />
    </Main>
  );
};

export default CreateBooking;
